import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Work as DesignationIcon,
  Refresh as RefreshIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material";
import AlertModal from "../../components/AlertModal";
import ConfirmModal from "../../components/ConfirmModal";
import { hasWritePermission } from '../../utils/menuFilter';

const DesignationList = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [designationData, setDesignationData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingDesignation, setEditingDesignation] = useState(null);
  const [newDesignation, setNewDesignation] = useState({
    name: "",
    fromAmount: "",
    toAmount: "",
    commission: "",
    level: "",
    fromSelfBusiness: "",
    toSelfBusiness: "",
    status: "Active",
  });

  // Modal states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDesignationForDelete, setSelectedDesignationForDelete] = useState(null);

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const fetchDesignations = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(`${baseURL}/api/globalMasterDetails?type=designation`);
      if (!response.ok) {
        throw new Error(`Failed to fetch designations: ${response.statusText}`);
      }
      const responseData = await response.json();
      const designations = Array.isArray(responseData.data)
        ? responseData.data.map((item) => ({
          id: item._id,
          details: {
            designationName: item.details.designationName || "N/A",
            description: item.details.description || "No description available",
            commission: item.details.commission || 0,
            fromAmount: item.details.fromAmount || item.details.fromTarget || 0,
            toAmount: item.details.toAmount || item.details.toTarget || 0,
            fromSelfBusiness: item.details.fromSelfBusiness || 0,
            toSelfBusiness: item.details.toSelfBusiness || 0,
            level: item.details.level || item.details.rank || "",
          },
          status: item.status ? "Active" : "Inactive",
        }))
        : [];
      setDesignationData(designations);
    } catch (error) {
      setApiError("Failed to fetch designations. Please try again later.");
      console.error("Error fetching designations:", error);
      setDesignationData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDesignations();
  }, []);

  const getFilteredDesignations = () => {
    if (!Array.isArray(designationData)) {
      console.warn("designationData is not an array:", designationData);
      return [];
    }

    return designationData.filter((designation) => {
      const matchesSearch = Object.values(designation.details || {}).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || designation.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Active":
        return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
      case "Inactive":
        return { backgroundColor: "#ffebee", color: "#d32f2f" };
      default:
        return { backgroundColor: "#f5f5f5", color: "#666" };
    }
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newDesignation.name.trim()) newErrors.name = "Designation name is required";
    if (newDesignation.fromAmount === "" || newDesignation.fromAmount === null) newErrors.fromAmount = "From amount is required";
    if (newDesignation.toAmount === "" || newDesignation.toAmount === null) newErrors.toAmount = "To amount is required";
    if (newDesignation.commission === "" || newDesignation.commission === null) newErrors.commission = "Commission is required";
    if (newDesignation.fromSelfBusiness === "" || newDesignation.fromSelfBusiness === null) newErrors.fromSelfBusiness = "From Self Business is required";
    if (newDesignation.toSelfBusiness === "" || newDesignation.toSelfBusiness === null) newErrors.toSelfBusiness = "To Self Business is required";
    if (!newDesignation.level) newErrors.level = "Level is required";

    if (Number(newDesignation.toAmount) <= Number(newDesignation.fromAmount)) {
      newErrors.toAmount = "To amount must be greater than from amount";
    }

    if (Number(newDesignation.toSelfBusiness) <= Number(newDesignation.fromSelfBusiness)) {
      // Allow 0-0 range, but for any other values, To must be > From
      if (Number(newDesignation.toSelfBusiness) !== 0 || Number(newDesignation.fromSelfBusiness) !== 0) {
        newErrors.toSelfBusiness = "To Self Business must be greater than from self business";
      }
    }

    // Validation to maintain continuity with existing designations
    if (!editingDesignation && designationData.length > 0) {
      const sorted = [...designationData].sort((a, b) => Number(a.details.level) - Number(b.details.level));
      const last = sorted[sorted.length - 1].details;
      const lastToAmount = Number(last.toAmount);

      if (Number(newDesignation.fromAmount) < lastToAmount) {
        newErrors.fromAmount = `From amount cannot be less than the last designation's toAmount (${lastToAmount})`;
      }

      if (Number(newDesignation.level) <= Number(last.level)) {
        newErrors.level = `Level must be greater than the last designation's level (${last.level})`;
      }
    }

    return newErrors;
  };

  const handleEdit = (designation) => {
    setEditingDesignation(designation);
    setNewDesignation({
      name: designation.details.designationName || "",
      fromAmount: designation.details.fromAmount || "",
      toAmount: designation.details.toAmount || "",
      commission: designation.details.commission || "",
      level: designation.details.level || "",
      fromSelfBusiness: designation.details.fromSelfBusiness || "",
      toSelfBusiness: designation.details.toSelfBusiness || "",
      status: designation.status || "Active",
    });
    setOpenModal(true);
  };

  const handleDeleteClick = (designation) => {
    setSelectedDesignationForDelete(designation);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDesignationForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${baseURL}/api/globalMasterDetails/${selectedDesignationForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      await fetchDesignations(true);
      setAlertConfig({ title: 'Success', message: 'Designation deleted successfully!' });
      setAlertOpen(true);
    } catch (error) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete designation. Please try again.' });
      setAlertOpen(true);
      console.error("Error deleting designation:", error);
    } finally {
      setIsLoading(false);
      setSelectedDesignationForDelete(null);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    const count = getFilteredDesignations().length;
    setAlertConfig({
      title: 'Search Results',
      message: count > 0 ? `Found ${count} designations matching your criteria.` : 'No designations found matching your criteria.'
    });
    setAlertOpen(true);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPage(0);
    setErrors({});
    fetchDesignations(true);
  };

  const handleOpenModal = () => {
    setEditingDesignation(null);

    // Auto-calculate next level and fromAmount for convenience and consistency
    const sorted = [...designationData].sort((a, b) => Number(a.details.level) - Number(b.details.level));
    const last = sorted.length > 0 ? sorted[sorted.length - 1].details : null;

    setNewDesignation({
      name: "",
      fromAmount: last ? last.toAmount : 0,
      toAmount: "",
      commission: "",
      level: last ? Number(last.level) + 1 : 1,
      fromSelfBusiness: last ? last.toSelfBusiness : 0,
      toSelfBusiness: "",
      status: "Active",
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingDesignation(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDesignation({ ...newDesignation, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddDesignation = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: "designation",
        details: {
          designationName: newDesignation.name,
          fromAmount: Number(newDesignation.fromAmount),
          toAmount: Number(newDesignation.toAmount),
          commission: Number(newDesignation.commission),
          level: Number(newDesignation.level),
          fromSelfBusiness: Number(newDesignation.fromSelfBusiness),
          toSelfBusiness: Number(newDesignation.toSelfBusiness),
        },
        status: newDesignation.status === "Active",
        createdBy: "adminUser",
      };

      const url = editingDesignation
        ? `${baseURL}/api/globalMasterDetails/${editingDesignation.id}`
        : `${baseURL}/api/globalMasterDetails`;
      const method = editingDesignation ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to ${editingDesignation ? "update" : "add"} designation: ${response.statusText}`
        );
      }

      await fetchDesignations(true);
      handleCloseModal();
      setAlertConfig({
        title: 'Success',
        message: editingDesignation ? "Designation updated successfully!" : "Designation added successfully!"
      });
      setAlertOpen(true);
    } catch (error) {
      setErrors({ general: error.message || "An error occurred. Please try again." });
      console.error("Error saving designation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDesignations = getFilteredDesignations();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DesignationIcon sx={{ mr: 1, color: "primary.main", fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Designation Master Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchDesignations()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600, flex: { xs: 1, sm: 'none' }, textTransform: 'none' }}
            >
              Add New Designation
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal Alerts & Confirms */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the designation <strong>{selectedDesignationForDelete?.details.designationName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchDesignations()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Designations..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
              {/* <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Reset
              </Button> */}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Designation Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>From - To Amount</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Self Business (From-To)</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Commission</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Level</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDesignations.length > 0 ? (
                  filteredDesignations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((designation, index) => (
                      <TableRow key={designation.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{designation.details.designationName || "N/A"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {designation.details.fromAmount} - {designation.details.toAmount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {designation.details.fromSelfBusiness} - {designation.details.toSelfBusiness}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{designation.details.commission}%</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{designation.details.level}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={designation.status || "N/A"}
                            size="small"
                            style={getStatusStyle(designation.status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            {canWrite && (
                              <Tooltip title="Edit Designation">
                                <IconButton color="primary" size="small" onClick={() => handleEdit(designation)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canWrite && (
                              <Tooltip title="Delete Designation">
                                <IconButton color="error" size="small" onClick={() => handleDeleteClick(designation)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No designations found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDesignations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {editingDesignation ? "Edit Designation" : "Add New Designation"}
        </DialogTitle>
        <DialogContent sx={{ pt: 0, mx: 'auto' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Designation Name"
                name="name"
                value={newDesignation.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="From Amount"
                name="fromAmount"
                type="number"
                value={newDesignation.fromAmount}
                onChange={handleInputChange}
                error={!!errors.fromAmount}
                helperText={errors.fromAmount}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="To Amount"
                name="toAmount"
                type="number"
                value={newDesignation.toAmount}
                onChange={handleInputChange}
                error={!!errors.toAmount}
                helperText={errors.toAmount}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission (%)"
                name="commission"
                type="number"
                value={newDesignation.commission}
                onChange={handleInputChange}
                error={!!errors.commission}
                helperText={errors.commission}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Level"
                name="level"
                type="number"
                value={newDesignation.level}
                onChange={handleInputChange}
                error={!!errors.level}
                helperText={errors.level}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="From Self Business"
                name="fromSelfBusiness"
                type="number"
                value={newDesignation.fromSelfBusiness}
                onChange={handleInputChange}
                error={!!errors.fromSelfBusiness}
                helperText={errors.fromSelfBusiness}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="To Self Business"
                name="toSelfBusiness"
                type="number"
                value={newDesignation.toSelfBusiness}
                onChange={handleInputChange}
                error={!!errors.toSelfBusiness}
                helperText={errors.toSelfBusiness}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newDesignation.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddDesignation}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "Saving..." : editingDesignation ? "Update Designation" : "Add Designation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DesignationList;